import React, { useEffect, useContext, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataForm, validateMetadata } from '@jutro/legacy/uiconfig';
import { useTranslator } from '@jutro/locale';
import PropTypes from 'prop-types';
import { MLAppStateContext } from 'ml-portals-util-react';
import appConfig from 'app-config';
import classNames from 'classnames';
import { MLClickToChatService } from 'ml-services-common-react';
import MLFooterSubmissionInfoComponent from '../MLFooterSubmissionInfoComponent/MLFooterSubmissionInfoComponent';
import metadata from './CustomFooterComponent.metadata.json5';
import pemetadata from './PECustomFooterComponent.metadata.json5';
import styles from './CustomFooterComponent.module.scss';
import pestyles from './PECustomFooterComponent.module.scss';
import messages from './CustomFooterComponent.messages';
import pemessages from './PECustomFooterComponent.messages';
import MLLinks from '../../../../../quote-and-buy/src/config/ml-links.json';
import { MLPortalSettingsService } from 'gw-capability-quoteandbind';

const isQB = appConfig.portalCode === 'QB';

const MLFooterPhoneNumberComponent = (props) => {
    const { className, value } = props;
    if(isQB){
        return <a href={`tel:+${value}`} className={className} style={{fontSize: '16px', textDecoration: 'none'}}>{value}</a>
    }
    return <span className={className}>{value}</span>;
};

MLFooterPhoneNumberComponent.defaultProps = {
    className: undefined,
    value: undefined
};

MLFooterPhoneNumberComponent.propTypes = {
    className: PropTypes.string,
    value: PropTypes.string
};

const CustomFooterComponent = () => {
    const translator = useTranslator();

    useEffect(() => {
        validateMetadata(metadata);
    }, []);

    const { appState } = useContext(MLAppStateContext);

    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';
    const [farmersDisclosure, updateFarmersDisclosure] = useState(false);
    const [clickToChat, updateClickToChat] = useState(false);
    const { authHeader, authUserData } = useAuthentication();
    const { MLUserTypes } = appConfig;
    const [enableAskForemost, setEnableAskForemost] = useState(false)

    useEffect(() => {
        let isMounted = true;
        const loadAskForemostSettings = async () => {
            try {
                const portalSettings = await MLPortalSettingsService.getSettings(authHeader);
                if(!isMounted) return;
                const { enableAskForemost } = portalSettings;
                setEnableAskForemost(enableAskForemost)
            }
            catch {
                if(isMounted){
                    setEnableAskForemost(false)
                }
            }

        }
        loadAskForemostSettings()

        return () => {
            isMounted = false;
        }
    }, [authHeader]);


    let isEAorIAorMetGA = false;
    if (authUserData) {
        isEAorIAorMetGA = authUserData.userType === MLUserTypes.PCS || authUserData.userType === MLUserTypes.IA || authUserData.userType === MLUserTypes.MET_GA;
    }

    const {
        quoteID,
        sessionUUID,
        emailAddress1,
        phoneNumber,
        submissionStatus,
        policyNumber
    } = appState;

    const uiData = {
        'qnb.phone': '1-844-438-8430',
        'amp.phone': '1-800-422-4272'
    };

    const location = useLocation();

    useEffect(() => {
        if (typeof Invoca !== 'undefined') {
            Invoca.PNAPI.run();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isBaseWizard = location.pathname.startsWith('/quote-') || location.pathname.startsWith('/endorsement-') || location.pathname.startsWith('/cancellation-flow') || location.pathname.startsWith('/reinstatement-flow') || location.pathname.startsWith('/rewrite-flow');
    const isAccountOrPolicy = location.pathname.startsWith('/accounts') || location.pathname.startsWith('/policies');

    const waitForChatToDisplay = (selector, callback, checkFrequencyInMs, timeoutInMs) => {
        const startTimeInMs = Date.now();
        const search = () => {
            if (document.querySelector(selector) !== null) {
                callback();
            } else {
                setTimeout(() => {
                    if (timeoutInMs && Date.now() - startTimeInMs > timeoutInMs) {
                        console.error(new Error('Chat failed to load. Please try again later.'));
                        return;
                    }
                    search();
                }, checkFrequencyInMs);
            }
        };
        search();
    };

    const initClickToChat = useCallback(() => {
        MLClickToChatService.init(appConfig.portalCode, waitForChatToDisplay('.helpButtonEnabled', () => {
            document.getElementsByClassName('helpButtonEnabled')[0].click();
        }, 1000, 9000), authHeader);
    }, [authHeader]);

    const onAskForemostClick = () => {
        let url = '';
        if (window.location.hostname.includes('producer-engage-farmers')) {
            url = '/producer-engage/ask-foremost'
        } else {
            url = '/ask-foremost';
        }
        window.open(url, '_blank');
    };

    const getFullYear = new Date().getFullYear()

    const overrideProps = {
        farmersDisclosure: {
            visible: farmersDisclosure
        },
        footerCopyrightFarmers: {
            messageProps: {
                copyrightMessage: isQB? translator(messages.FarmersCopyrightQB, { year: getFullYear }) : (isAMP ? translator(messages.FarmersCopyrightAMP, { year: getFullYear }) : translator(messages.FarmersCopyright, { year: getFullYear }))
            }
        },
        footerCopyrightForemost: {
            visible: isAMP,
            messageProps: {
                copyrightMessage: translator(messages.ForemostCopyright, { year: getFullYear })
            }
        },
        mainFooterSocialMedia: {
            visible: false
        },
        mainFooterFaq: {
            visible: location.pathname !== '/faq'
        },
        mainFooterCallAgentPhoneNumber: {
            path: isAMP ? 'amp.phone' : 'qnb.phone',
            'aria-label': isQB ? 'Representative Phone Number' : '' 
        },
        subFooter: {
            className: isAMP ? "row main-footer__amp-sub-footer" : "row main-footer__sub-footer"
        },
        legalNotesNavLink: {
            mlTagManagerLinkClick: "footer_legalNotices",
            className: isQB ? "main-footer-content__legal-nav-item text-white" : "main-footer-content__legal-nav-item"
        },
        onlineTermsServiceNavLink: {
            mlTagManagerLinkClick: "footer_onlineTermOfService",
            className: isQB ? "main-footer-content__legal-nav-item text-white" : "main-footer-content__legal-nav-item"
        },
        OurCompanies: {
            className: isQB ? "main-footer-content__legal-nav-item text-white" : "main-footer-content__legal-nav-item",
            'aria-label': isQB ? "Our Companies (Opens in new tab)" : ""
        },
        subFooterSubmissionInfo: {
            submissionFound: !_.isUndefined(quoteID),
            quoteID,
            sessionUUID,
            submissionStatus,
            emailAddress1,
            phoneNumber,
            isOnQBContactDetails: location.pathname.endsWith('/your-info'),
            policyNumber,
            location: location.pathname
        },
        mainFooterChatAgentLink: {
            onClick: initClickToChat,
            mlTagManagerLinkClick: "Chat"
        },
        mainFooterChatAgent: {
            visible: clickToChat
        },
        mainFooterFaqLink: {
            mlTagManagerLinkClick: "FAQ",
            className: isQB ? "custom-text-link-color" : "",
            "aria-label": isQB ? "FAQ Website (Opens a new tab)" : "" 
        },
        fbIconLink: {
            "aria-label": isQB ? "Farmers Insurance Facebook Website (Opens in new tab)" : ""
        },
        twitterIconLink: {
            "aria-label": isQB ? "Farmers Insurance Twitter Website (Opens in new tab)" : ""
        },
        instagramIconLink: {
            "aria-label": isQB ? "Farmers Insurance Instagram Website (Opens in new tab)" : ""
        },
        linkedInIconLink: {
            "aria-label": isQB ? "Farmers Insurance LinkedIn Website (Opens in new tab)" : ""
        },
        privacyPolicyNavLink: {
            href: MLLinks.farmersPrivacyPolicy,
            mlTagManagerLinkClick: "footer_privacyPolicy",
            className: isQB ? "main-footer-content__legal-nav-item text-white" : "main-footer-content__legal-nav-item",
            'aria-label': isQB ? "Privacy Policy  (Opens in new tab)" : ""
        },
        personalInfoUseLink: {
            href: MLLinks.useOfPersonalInfo,
            mlTagManagerLinkClick: "footer_privacyPolicy",
            visible: isQB,
            className: isQB ? "main-footer-content__legal-nav-item text-white" : "main-footer-content__legal-nav-item",
            'aria-label': isQB ? "Privacy Policy - Purpose Consumer Section (Opens in new tab)" : ""
        },
        doNotSellLink: {
            href: MLLinks.farmersDoNotSellLink,
            visible: isQB,
            mlTagManagerLinkClick: "footer_privacyPolicy",
            className: isQB ? "main-footer-content__legal-nav-item text-white" : "main-footer-content__legal-nav-item",
            'aria-label': isQB ? "Privacy Policy - Do not sell Section (Opens in new tab)" : ""
        },
        linkLineBreak: {
            visible: isQB
        },
        mainFooterCallAgentAvailability3: {
            visible: isAMP
        },
        ampMainFooterCallAgentSaturdayTime: {
            visible: isAMP
        },
        qbMainFooterCallAgentSaturdayTime: {
            visible: isQB
        }
    };

    const peoverrideProps = {
        footerCopyrightFarmers: {
            visible: false
        },
        footerCopyrightForemost: {
            messageProps: {
                copyrightMessage: translator(messages.ForemostCopyright, { year: new Date().getFullYear() })
            }
        },
        wizardAccountPolicyContainer: {
            visible: isBaseWizard || isAccountOrPolicy,
            className: isAccountOrPolicy ? classNames("ml-left-rail-footer", "white") : 'ml-left-rail-footer'
        },
        otherContainer: {
            visible: !isBaseWizard && !isAccountOrPolicy
        },
        leftColumn: {
            className: isAccountOrPolicy ? classNames("left-part", "light") : 'left-part'
        },
        mainFooterRequestCallLink: {
            mlTagManagerLinkClick: "Search for an Agent"
        },
        footerCopyright: {
            messageProps: {
                copyrightMessage: translator(pemessages.Copyright)
            }
        },
        askForemostButton: {
            visible: enableAskForemost,
            onClick: onAskForemostClick,
            title: 'Opens in a new tab/window'
        },
        legalNotesNavLink: {
            title: 'Opens in a new tab/window'
        },
        privacyPolicyNavLink: {
            title: 'Opens in a new tab/window'
        },
        onlineTermsServiceNavLink: {
            title: 'Opens in a new tab/window'
        }
    }

    const componentMap = {
        MLFooterPhoneNumberComponent: MLFooterPhoneNumberComponent,
        MLFooterSubmissionInfoComponent: MLFooterSubmissionInfoComponent
    };

    if (location.pathname === '/bundle-cart') {
        return <React.Fragment />
    }

    return (
        <MetadataForm
            uiProps={isGPA ? pemetadata.componentContent : metadata.componentContent}
            classNameMap={isGPA ? pestyles : styles}
            data={uiData}
            overrideProps={isGPA ? peoverrideProps : overrideProps}
            componentMap={componentMap}
            isUsingNewValidation />
    );
};

CustomFooterComponent.defaultProps = {
};

CustomFooterComponent.propTypes = {

};
export default CustomFooterComponent;
